// Google Ads Editor-compatible CSV builders.
// The RSA CSV mirrors the "Ads" import template (Responsive Search Ad).
// The Keywords CSV mirrors the "Keywords" import template.
// Both use CRLF line endings and RFC-4180 quoting for Excel/Editor safety.

import { AD_ASSET_STOP_LABELS } from "./adAssetGuards.js";
import { sanitizeAdAsset } from "./sanitizeAsset.js";

const csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
};

const toCsv = (rows) =>
    rows.map((r) => r.map(csvEscape).join(",")).join("\r\n") + "\r\n";

const slug = (s) =>
    (s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 40) || "export";

const campaignName = (r) =>
    `${r.business_type} - ${r.city} (${(r.country || "").toUpperCase()})`;

const adGroupName = (r) => `${r.business_type} - Core`;

/**
 * Builds a Google Ads Editor CSV for a single Responsive Search Ad.
 * Columns follow the Editor's Ads import schema.
 */
export const buildRsaCsv = (r) => {
    const ai = r.ai || {};
    const headlines = ai.headlines || [];
    const descriptions = ai.descriptions || [];

    // Pad to 15 headlines / 4 descriptions (Editor accepts blanks).
    const H = Array.from({ length: 15 }, (_, i) => headlines[i] || "");
    const D = Array.from({ length: 4 }, (_, i) => descriptions[i] || "");

    const header = [
        "Campaign",
        "Ad group",
        "Ad status",
        "Ad type",
        ...H.flatMap((_, i) => [`Headline ${i + 1}`, `Headline ${i + 1} position`]),
        ...D.flatMap((_, i) => [`Description ${i + 1}`, `Description ${i + 1} position`]),
        "Path 1",
        "Path 2",
        "Final URL",
    ];

    const row = [
        campaignName(r),
        adGroupName(r),
        "Paused",
        "Responsive search ad",
        ...H.flatMap((h) => [h, ""]),
        ...D.flatMap((d) => [d, ""]),
        slug(r.business_type).slice(0, 15),
        slug(r.city).slice(0, 15),
        "https://example.com/",
    ];

    return toCsv([header, row]);
};

/**
 * Builds a Google Ads Editor CSV for the inferred competitor keywords,
 * one row per keyword, defaulting to Phrase match.
 */
export const buildKeywordsCsv = (r) => {
    const kws = (r.ai && r.ai.keywords) || [];
    const header = [
        "Campaign",
        "Ad group",
        "Keyword",
        "Match type",
        "Status",
    ];
    const rows = kws.map((k) => [
        campaignName(r),
        adGroupName(r),
        k,
        "Phrase",
        "Paused",
    ]);
    return toCsv([header, ...rows]);
};

/**
 * Parse a blob of text (CSV file contents, textarea paste, comma list, etc.)
 * into a deduped, trimmed list of keywords. RFC-4180 compliant: quoted fields
 * containing commas or embedded quotes are preserved as a single token.
 * Skips a common header row like "keyword" / "keywords" if it's the first token.
 */
export const parseKeywordText = (raw) => {
    if (!raw) return [];
    const text = String(raw);

    // Tokenize using a mini CSV parser (handles quoted fields with commas + "" escapes).
    const tokens = [];
    let field = "";
    let inQuotes = false;
    let sawContent = false;

    const flushField = () => {
        if (sawContent) tokens.push(field);
        field = "";
        sawContent = false;
    };

    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    sawContent = true;
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += c;
                sawContent = true;
            }
        } else {
            if (c === '"') {
                inQuotes = true;
                sawContent = true; // quoted field, even if empty inside
            } else if (c === ",") {
                flushField();
            } else if (c === "\n" || c === "\r") {
                flushField();
                if (c === "\r" && text[i + 1] === "\n") i++;
            } else {
                field += c;
                if (c.trim()) sawContent = true;
            }
        }
    }
    flushField();

    const out = [];
    const seen = new Set();
    tokens.forEach((tok, idx) => {
        const s = tok.trim();
        if (!s) return;
        if (idx === 0 && /^(keyword|keywords|term|terms|query)$/i.test(s)) return;
        const key = s.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push(s);
    });
    return out;
};

export const downloadCsv = (filename, content) => {
    const blob = new Blob(["\ufeff" + content], {
        type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
};

export const rsaFilename = (r) =>
    `serpscout_rsa_${slug(r.business_type)}_${slug(r.city)}.csv`;

export const keywordsFilename = (r) =>
    `serpscout_keywords_${slug(r.business_type)}_${slug(r.city)}.csv`;

export const adsFilename = (r) =>
    `serpscout_competitor_ads_${slug(r.business_type)}_${slug(r.city)}.csv`;

export const relatedFilename = (r) =>
    `serpscout_related_searches_${slug(r.business_type)}_${slug(r.city)}.csv`;

export const campaignFilename = (r) =>
    `serpscout_campaign_${slug(r.business_type)}_${slug(r.city)}.csv`;

/**
 * Google Ads Editor-compatible multi-entity campaign CSV.
 *
 * Emits four kinds of rows in one file:
 *   1. Campaign      — with budget, network, locations, language, bid strategy
 *   2. Ad Group (×N) — one per theme (Seed / Related / Competitor-Inferred)
 *   3. Keyword       — every phrase × 2 rows (Phrase + Exact match)
 *   4. Ad            — one Responsive Search Ad per ad group, using AI headlines + descriptions
 *
 * `config` fields:
 *   businessName  — used for campaign name display
 *   websiteUrl    — Final URL on every RSA + Path 1/2 hints
 *   dailyBudget   — number (in the account's currency)
 *   extraSeeds    — additional seed keywords (array of strings), merged with r.business_type
 *   currency      — optional label only used for the confirmation toast
 */
/**
 * Compute the four keyword pools (Custom > Seed > Related > Inferred) with a
 * cascade dedupe so a keyword only ever lives in the highest-priority pool.
 * Shared by buildCampaignCsv (CSV emission) and the Campaign Builder dialog
 * (row preview), so the two can never diverge.
 */
export const computeKeywordPools = (r, config = {}) => {
    const extraSeeds = Array.isArray(config.extraSeeds) ? config.extraSeeds : [];
    const customKeywords = Array.isArray(config.customKeywords) ? config.customKeywords : [];

    const norm = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
    const dedupe = (arr) => Array.from(new Set(arr.map(norm).filter(Boolean)));

    // Merge every keyword across every competitor page into one pool.
    const competitorRaw = [];
    for (const p of r?.competitor_pages || []) {
        if (p && Array.isArray(p.keywords)) competitorRaw.push(...p.keywords);
    }

    const customPool = dedupe(customKeywords);
    const customSet = new Set(customPool);
    const competitorPool = dedupe(competitorRaw).filter((k) => !customSet.has(k));
    const compSet = new Set([...customPool, ...competitorPool]);
    const seedPool = dedupe([r?.business_type, ...extraSeeds]).filter((k) => !compSet.has(k));
    const seedSet = new Set([...compSet, ...seedPool]);
    const relatedPool = dedupe(r?.related_searches || []).filter((k) => !seedSet.has(k));
    const combinedSet = new Set([...seedSet, ...relatedPool]);
    const inferredPool = dedupe((r?.ai && r.ai.keywords) || []).filter((k) => !combinedSet.has(k));

    return { customPool, competitorPool, seedPool, relatedPool, inferredPool };
};

export const buildCampaignCsv = (r, config = {}) => {
    const businessName = (config.businessName || r.business_type || "").trim();
    const websiteUrl = (config.websiteUrl || "https://example.com/").trim();
    const dailyBudget = String(config.dailyBudget || "20").trim();
    const trust = config.trustCopy || { headlines: [], descriptions: [], callouts: [] };

    const campaignName = `${businessName} - ${r.city} (${(r.country || "").toUpperCase()})`;
    const location = `${r.city}, ${(r.country || "").toUpperCase()}`;

    // Build the keyword pools via the shared cascade helper (Custom > Competitor > Seed > Related > Inferred).
    const { customPool, competitorPool, seedPool, relatedPool, inferredPool } = computeKeywordPools(r, config);

    const ai = r.ai || {};
    const aiHeadlines = ai.headlines || [];
    const aiDescriptions = ai.descriptions || [];

    // Build a theme -> {headlines, descriptions} lookup with fallback to top-level.
    const themedIndex = {};
    for (const t of ai.themed_ads || []) {
        if (t && t.theme) themedIndex[String(t.theme).toLowerCase()] = t;
    }
    const getThemeCopy = (themeSlug) => {
        // "custom" is a synthetic slug — it means "use the top-level framework copy only".
        // Keeps the Custom ad group's RSA distinct from Seed's RSA.
        if (themeSlug === "custom") {
            return {
                headlines: aiHeadlines,
                descriptions: aiDescriptions,
            };
        }
        const t = themedIndex[themeSlug];
        return {
            headlines: (t && t.headlines && t.headlines.length ? t.headlines : aiHeadlines) || [],
            descriptions: (t && t.descriptions && t.descriptions.length ? t.descriptions : aiDescriptions) || [],
        };
    };

    // Section-label words that must NEVER end up as an ad asset in the CSV.
    // Belt-and-braces guard so a stale trust-copy payload can't leak "Descriptions" etc.
    // Set imported from ./adAssetGuards so it stays in sync with trustCopy.js's router.
    const STOP_LABELS = AD_ASSET_STOP_LABELS;

    // Merge AI + trust-copy assets, dedupe case-insensitively, respect Google Ads slot caps.
    // Every string flows through sanitizeAdAsset first — this rejects blocklist matches
    // (incl. typos like "Decriptions") AND rewrites mid-word chops via word-safe truncation.
    const mergeUnique = (primary, extra, maxLen, cap) => {
        const out = [];
        const seen = new Set();
        const push = (raw) => {
            const s = sanitizeAdAsset(raw, maxLen);
            if (!s) return;
            const k = s.toLowerCase();
            if (STOP_LABELS.has(k)) return;
            if (seen.has(k)) return;
            seen.add(k);
            if (out.length < cap) out.push(s);
        };
        for (const x of primary) push(x);
        for (const x of extra) push(x);
        return out;
    };

    // Headlines: AI's 5 first (question-hook order kept), then trust copy, cap 15.
    const mergedHeadlines = mergeUnique(aiHeadlines, trust.headlines || [], 30, 15);
    // Descriptions: AI's up to 3 first (desc #1 is the question hook — do not reorder), then trust copy, cap 4.
    const mergedDescriptions = mergeUnique(aiDescriptions, trust.descriptions || [], 90, 4);

    const H = Array.from({ length: 15 }, (_, i) => mergedHeadlines[i] || "");
    const D = Array.from({ length: 4 }, (_, i) => mergedDescriptions[i] || "");

    // ------- Column schema (Google Ads Editor unified multi-entity import) -------
    const header = [
        "Campaign",
        "Campaign type",
        "Campaign status",
        "Budget",
        "Budget type",
        "Bid strategy type",
        "Networks",
        "Locations",
        "Language",
        "Ad group",
        "Ad group status",
        "Ad group type",
        "Max CPC",
        "Keyword",
        "Criterion type",
        "Match type",
        "Ad type",
        "Ad status",
        ...H.flatMap((_, i) => [`Headline ${i + 1}`, `Headline ${i + 1} position`]),
        ...D.flatMap((_, i) => [`Description ${i + 1}`, `Description ${i + 1} position`]),
        "Path 1",
        "Path 2",
        "Final URL",
        // Asset extension columns (used only by sitelink / callout rows)
        "Asset type",
        "Sitelink text",
        "Sitelink description 1",
        "Sitelink description 2",
        "Callout text",
    ];

    const blank = () => Array(header.length).fill("");

    const rows = [header];

    // ---- 1. Campaign row ----
    const camp = blank();
    camp[0] = campaignName;
    camp[1] = "Search"; // Campaign type
    camp[2] = "Paused"; // Campaign status
    camp[3] = dailyBudget;
    camp[4] = "Daily";
    camp[5] = "Manual CPC";
    camp[6] = "Google search";
    camp[7] = location;
    camp[8] = "English";
    rows.push(camp);

    // ---- 2. Ad group rows + 3. Keyword rows + 4. Ad row (one RSA per group) ----
    // themeSlug determines which RSA copy stack each group uses:
    //   "custom"    → top-level framework copy ONLY (differentiated from seed)
    //   "seed"      → themed_ads.seed  (seed-intent copy)
    //   "related"   → themed_ads.related  (variant-intent copy)
    //   "inferred"  → themed_ads.inferred  (brand/competitor-intent copy)
    // Custom + Competitor Page both use their own distinct copy stacks so no two ad groups share
    // an identical RSA.
    const groups = [
        { name: `${businessName} - Custom`, keywords: customPool, themeSlug: "custom" },
        { name: `${businessName} - Competitor Page`, keywords: competitorPool, themeSlug: "inferred" },
        { name: `${businessName} - Seed`, keywords: seedPool, themeSlug: "seed" },
        { name: `${businessName} - Related`, keywords: relatedPool, themeSlug: "related" },
        { name: `${businessName} - Competitor Inferred`, keywords: inferredPool, themeSlug: "inferred" },
    ];

    const slugPath = (s) =>
        (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 15);
    const path1 = slugPath(businessName || r.business_type);
    const path2 = slugPath(r.city);

    for (const g of groups) {
        // Skip empty groups
        if (!g.keywords || g.keywords.length === 0) continue;

        // 2a. Ad group row
        const ag = blank();
        ag[0] = campaignName;
        ag[9] = g.name;
        ag[10] = "Paused";
        ag[11] = "Standard";
        rows.push(ag);

        // 3. Keyword rows — Phrase + Exact for each phrase
        for (const kw of g.keywords) {
            for (const [matchType, wrapped] of [
                ["Phrase", `"${kw}"`],
                ["Exact", `[${kw}]`],
            ]) {
                const kr = blank();
                kr[0] = campaignName;
                kr[9] = g.name;
                kr[13] = wrapped;
                kr[14] = "Keyword";
                kr[15] = matchType;
                rows.push(kr);
            }
        }

        // 4. Ad row (RSA) — one per ad group, using theme-specific copy
        const themed = getThemeCopy(g.themeSlug);
        // Merge themed AI headlines with the (shared) trust-copy headlines. Theme copy first.
        const themedHeadlines = mergeUnique(themed.headlines, trust.headlines || [], 30, 15);
        const themedDescriptions = mergeUnique(themed.descriptions, trust.descriptions || [], 90, 4);
        const HH = Array.from({ length: 15 }, (_, i) => themedHeadlines[i] || "");
        const DD = Array.from({ length: 4 }, (_, i) => themedDescriptions[i] || "");

        const ad = blank();
        ad[0] = campaignName;
        ad[9] = g.name;
        ad[16] = "Responsive search ad";
        ad[17] = "Paused";
        // Headlines
        let col = 18;
        for (let i = 0; i < 15; i++) {
            ad[col++] = HH[i]; // Headline i+1
            ad[col++] = ""; // Headline i+1 position (blank = unpinned)
        }
        // Descriptions
        for (let i = 0; i < 4; i++) {
            ad[col++] = DD[i];
            ad[col++] = "";
        }
        ad[col++] = path1;
        ad[col++] = path2;
        ad[col++] = websiteUrl;
        rows.push(ad);
    }

    // ---- 5. Campaign-level negative keywords ----
    const negNorm = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
    const negatives = Array.from(
        new Set(((r.ai && r.ai.negatives) || []).map(negNorm).filter(Boolean))
    );
    for (const neg of negatives) {
        const nr = blank();
        nr[0] = campaignName;
        nr[13] = neg;             // Keyword text (unquoted; Editor treats negatives as broad by default)
        nr[14] = "Negative keyword";
        nr[15] = "Phrase";        // Phrase-match negative is the safest default
        rows.push(nr);
    }

    // ---- 6. Sitelink assets (campaign-level) ----
    const sitelinks = ((r.ai && r.ai.sitelinks) || []).filter((s) => s && s.text);
    // Header index for asset columns
    const A_TYPE = header.indexOf("Asset type");
    const A_SL_TEXT = header.indexOf("Sitelink text");
    const A_SL_D1 = header.indexOf("Sitelink description 1");
    const A_SL_D2 = header.indexOf("Sitelink description 2");
    const A_CO_TEXT = header.indexOf("Callout text");
    const A_FINAL_URL = header.indexOf("Final URL");

    for (const sl of sitelinks) {
        const sr = blank();
        sr[0] = campaignName;
        sr[A_TYPE] = "Sitelink";
        sr[A_SL_TEXT] = (sl.text || "").slice(0, 25);
        sr[A_SL_D1] = (sl.description1 || "").slice(0, 35);
        sr[A_SL_D2] = (sl.description2 || "").slice(0, 35);
        sr[A_FINAL_URL] = websiteUrl;
        rows.push(sr);
    }

    // ---- 7. Callout assets (campaign-level) ----
    // Case-preserving dedupe. Merge AI callouts + trust-copy callouts, cap at Google Ads' campaign callout limit (20).
    const seenCallouts = new Set();
    const mergeCallouts = (raw) => {
        const s = sanitizeAdAsset(raw, 25);
        if (!s) return null;
        const k = s.toLowerCase();
        if (STOP_LABELS.has(k)) return null;
        if (seenCallouts.has(k)) return null;
        seenCallouts.add(k);
        return s;
    };
    const callouts = [];
    for (const c of (r.ai && r.ai.callouts) || []) {
        const kept = mergeCallouts(c);
        if (kept && callouts.length < 20) callouts.push(kept);
    }
    for (const c of trust.callouts || []) {
        const kept = mergeCallouts(c);
        if (kept && callouts.length < 20) callouts.push(kept);
    }
    for (const co of callouts) {
        const cr = blank();
        cr[0] = campaignName;
        cr[A_TYPE] = "Callout";
        cr[A_CO_TEXT] = (co || "").slice(0, 25);
        rows.push(cr);
    }

    return toCsv(rows);
};

/**
 * Builds a CSV of the related searches surfaced by Google for this query.
 */
export const buildRelatedCsv = (r) => {
    const related = r.related_searches || [];
    const header = ["Business", "City", "Country", "Related Search"];
    const rows = related.map((q) => [
        r.business_type,
        r.city,
        (r.country || "").toUpperCase(),
        q,
    ]);
    return toCsv([header, ...rows]);
};

/**
 * Builds a CSV of the competitor ads currently on screen: one row per ad,
 * with headline, description, displayed link, source (serp/transparency),
 * and last-shown date.
 */
export const buildAdsCsv = (r) => {
    const ads = r.ads || [];
    const header = [
        "Business",
        "City",
        "Country",
        "Headline",
        "Description",
        "Displayed URL",
        "Source",
        "First shown",
        "Last shown",
        "Transparency URL",
    ];
    const rows = ads.map((a) => [
        r.business_type,
        r.city,
        (r.country || "").toUpperCase(),
        a.title || "",
        a.description || "",
        a.displayed_link || "",
        a.source || "",
        a.first_shown || "",
        a.last_shown || "",
        a.link || "",
    ]);
    return toCsv([header, ...rows]);
};

/**
 * Copies text to the user's clipboard. Uses the Clipboard API when
 * available and falls back to a hidden textarea + execCommand.
 * Returns true on success.
 */
export const copyToClipboard = async (text) => {
    if (!text) return false;
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (e) {
        // fall through to legacy path
    }
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (e) {
        return false;
    }
};
